import { useState, useEffect } from "react";
import { useAuth } from "@clerk/react";
import { useNavigate, Link } from "@tanstack/react-router";
import { useBillingStatus } from "../hooks/useBillingStatus.js";
import { getTeams, createTeam, getTeamMembers, inviteTeamMember, removeTeamMember } from "../api.js";
import type { Team, TeamMember } from "@tuit/shared-types";
import styles from "../styles/pages/SettingsPage.module.css";

export function TeamPage() {
  const { isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const { status: billing } = useBillingStatus();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [teamName, setTeamName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (isLoaded && !isSignedIn) navigate({ to: "/" });
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (!isSignedIn) return;
    getTeams().then((t) => { setTeams(t); if (t.length > 0) setSelectedTeam(t[0] ?? null); }).catch(() => {});
  }, [isSignedIn]);

  useEffect(() => {
    if (!selectedTeam) return;
    getTeamMembers(selectedTeam.id).then(setMembers).catch(() => {});
  }, [selectedTeam]);

  if (!isLoaded || !isSignedIn) return null;

  const canTeams = billing?.capabilities.canTeams ?? false;

  function flash(text: string) { setMsg(text); setTimeout(() => setMsg(null), 3500); }

  async function handleCreateTeam(e: React.FormEvent) {
    e.preventDefault();
    if (!teamName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const team = await createTeam({ name: teamName.trim() });
      setTeams((prev) => [...prev, team]);
      setSelectedTeam(team);
      setTeamName("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create team");
    } finally {
      setCreating(false);
    }
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteEmail.trim() || !selectedTeam) return;
    setInviting(true);
    setError(null);
    try {
      await inviteTeamMember(selectedTeam.id, inviteEmail.trim());
      const updated = await getTeamMembers(selectedTeam.id);
      setMembers(updated);
      setInviteEmail("");
      flash("Invite sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send invite");
    } finally {
      setInviting(false);
    }
  }

  async function handleRemove(member: TeamMember) {
    if (!selectedTeam) return;
    setBusyId(member.userId);
    try {
      await removeTeamMember(selectedTeam.id, member.userId);
      setMembers((prev) => prev.filter((m) => m.userId !== member.userId));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className={styles.page}>
      <nav className={styles.topbar}>
        <Link to="/profile" className={styles.back}>← Profile</Link>
      </nav>
      <div className={styles.inner}>
        <h1 className={styles.heading}>Team</h1>
        <p className={styles.sub}>Share rooms and collaborate with teammates.</p>

        {!canTeams ? (
          <div className={styles.upsell}>
            <p>Team workspaces are available on the <strong>Pro</strong> plan.</p>
            <Link to="/premium" className={styles.upsellLink}>Upgrade now →</Link>
          </div>
        ) : (
          <>
            {teams.length === 0 ? (
              <form className={styles.card} onSubmit={handleCreateTeam}>
                <p className={styles.hint}>You don't have a team yet. Create one to invite teammates.</p>
                <div className={styles.inlineFields}>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="Team name"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                  />
                  <button type="submit" className={styles.primaryBtn} disabled={creating || !teamName.trim()}>
                    {creating ? "Creating…" : "Create team"}
                  </button>
                </div>
                {error && <p className={styles.error}>{error}</p>}
              </form>
            ) : (
              <>
                {teams.length > 1 && (
                  <div className={styles.field}>
                    <select className={styles.input} value={selectedTeam?.id ?? ""} onChange={(e) => setSelectedTeam(teams.find((t) => t.id === e.target.value) ?? null)}>
                      {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                )}

                {selectedTeam && (
                  <>
                    <div className={styles.sectionHeader}>
                      <strong>{selectedTeam.name}</strong>
                      <span className={styles.hint}>{selectedTeam.seatCount} member{selectedTeam.seatCount !== 1 ? "s" : ""}</span>
                    </div>

                    <form className={styles.card} onSubmit={handleInvite}>
                      <div className={styles.inlineFields}>
                        <input
                          type="email"
                          className={styles.input}
                          placeholder="Invite by email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                        />
                        <button type="submit" className={styles.primaryBtn} disabled={inviting || !inviteEmail.trim()}>
                          {inviting ? "Sending…" : "Invite"}
                        </button>
                      </div>
                      {error && <p className={styles.error}>{error}</p>}
                    </form>

                    {members.length > 0 && (
                      <ul className={styles.list}>
                        {members.map((m) => (
                          <li key={m.userId} className={styles.listItem}>
                            <div className={styles.listItemMain}>
                              <span className={styles.listItemText}>{m.inviteEmail ?? m.userId}</span>
                              <span className={styles.listItemMeta}>
                                {m.inviteAcceptedAt ? m.role : "Pending invite"}
                              </span>
                            </div>
                            <button type="button" className={styles.deleteBtn} onClick={() => handleRemove(m)} disabled={busyId === m.userId}>
                              {busyId === m.userId ? "…" : "Remove"}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}

        {msg && <p className={styles.flash}>{msg}</p>}
      </div>
    </div>
  );
}
